import { Button, ButtonText } from '@/ui/core/button'
import { Rise } from '@/ui/core/motion'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useState } from 'react'
import { Text as RNText, View } from 'react-native'
import { StepLayout } from '../StepLayout'
import { type Paragraph, parseConsentText } from '../logic/consentText'
import { resolveButtonLabel } from '../logic/stepState'
import { PrimaryStepButton } from './PrimaryStepButton'
import type { StepProps } from './types'

const ConsentParagraphs = ({ paragraphs }: { paragraphs: Paragraph[] }) => (
	<VStack gap={14}>
		{paragraphs.map((runs, index) => (
			<Text
				// Paragraphs have no identity beyond their position in a fixed text.
				key={`${index}-${runs[0]?.text.slice(0, 16)}`}
				size="medium"
				weight="light"
				className="text-ink leading-6"
			>
				{runs.map((run, runIndex) => (
					<RNText
						key={`${runIndex}-${run.text.slice(0, 16)}`}
						className={run.bold ? 'font-bold' : undefined}
					>
						{run.text}
					</RNText>
				))}
			</Text>
		))}
	</VStack>
)

/**
 * Text to read and a button that acknowledges it.
 *
 * The long consents open on a short preview with "Read more"; the patient can go on without
 * opening it, as they could on the web, since the answer recorded is the acknowledgement.
 *
 * It reads on the cream note, which is the app's surface for a page of words, rather than on the
 * pink ground where a consent's length has nothing holding it.
 */
export const ConsentStep = ({
	step,
	onNext,
	onBack,
	onClose,
	progress,
	isSubmitting,
}: StepProps) => {
	const [isExpanded, setIsExpanded] = useState(!step.isExpandable)
	const paragraphs = parseConsentText(isExpanded ? step.text : step.preview)
	// Some consents are the whole notice in their description and carry no text of their own. Its
	// words are still the thing being consented to, so they read on the note where every other
	// consent's do, rather than as a line under the title with nothing beneath it.
	const inBody = paragraphs.length === 0 && !!step.description

	return (
		<StepLayout
			title={step.title}
			description={inBody ? undefined : step.description}
			progress={progress}
			surface="note"
			onBack={onBack}
			onClose={onClose}
			cta={
				<PrimaryStepButton
					label={resolveButtonLabel(step, undefined)}
					onPress={onNext}
					isLoading={isSubmitting}
				/>
			}
		>
			{inBody && (
				<Rise>
					<Text size="body0">{step.description}</Text>
				</Rise>
			)}
			{paragraphs.length > 0 && (
				<VStack gap={10}>
					<Rise>
						<ConsentParagraphs paragraphs={paragraphs} />
					</Rise>
					{step.isExpandable && !isExpanded && (
						<View className="items-start">
							<Button variant="link" onPress={() => setIsExpanded(true)}>
								<ButtonText>Read more</ButtonText>
							</Button>
						</View>
					)}
				</VStack>
			)}
		</StepLayout>
	)
}
